/**
 * User Collections Service
 * 
 * Manages user's NFT collections in Firestore.
 * Each collection maps to a deployed UnitCollection contract.
 * 
 * Firestore structure:
 *   userCollections/{docId}
 *     - userId: string (creator's Firebase UID)
 *     - walletAddress: string (creator's wallet that owns the collection)
 *     - contractAddress: string (deployed UnitCollection contract)
 *     - name: string (collection name, e.g., "Sarah's Art")
 *     - symbol: string (collection symbol, e.g., "SARAH")
 *     - chainId: string (hex chain ID, e.g., "0x2105" for Base)
 *     - royaltyBps: number (royalty in basis points, e.g., 500 = 5%)
 *     - contractURI: string (optional IPFS URI for collection metadata)
 *     - createdAt: timestamp
 *     - txHash: string (deployment transaction hash)
 */

import {
  collection,
  doc,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
} from "@/services/legacyBrowserPersistenceBoundary";

const COLLECTIONS_PATH = "userCollections";

/**
 * Save a newly deployed collection to Firestore
 * @param {Object} params Collection details
 * @param {string} params.userId Firebase user ID
 * @param {string} params.walletAddress Creator's wallet address
 * @param {string} params.contractAddress Deployed contract address
 * @param {string} params.name Collection name
 * @param {string} params.symbol Collection symbol
 * @param {string} params.chainId Chain ID in hex (e.g., "0x2105")
 * @param {number} params.royaltyBps Royalty in basis points
 * @param {string} [params.contractURI] Optional collection metadata URI
 * @param {string} params.txHash Deployment transaction hash
 * @returns {Promise<string>} Document ID
 */
export async function saveUserCollection({
  userId,
  walletAddress,
  contractAddress,
  name,
  symbol,
  chainId,
  royaltyBps,
  contractURI = "",
  txHash,
}) {
  if (!userId) throw new Error("userId is required");
  if (!walletAddress) throw new Error("walletAddress is required");
  if (!contractAddress) throw new Error("contractAddress is required");
  if (!name) throw new Error("name is required");
  if (!chainId) throw new Error("chainId is required");

  const collectionData = {
    userId,
    walletAddress: walletAddress.toLowerCase(),
    contractAddress: contractAddress.toLowerCase(),
    name,
    symbol: symbol || name.substring(0, 8).toUpperCase().replace(/\s/g, ""),
    chainId: chainId.toLowerCase(),
    royaltyBps: royaltyBps || 500,
    contractURI: contractURI || "",
    txHash: txHash || "",
    createdAt: serverTimestamp(),
  };

  const docRef = await addDoc(collection(null, COLLECTIONS_PATH), collectionData);
  console.log("[UserCollections] Saved collection:", docRef.id, name);
  return docRef.id;
}

/**
 * Get all collections owned by a user
 * @param {string} userId Firebase user ID
 * @param {string} [chainId] Optional filter by chain
 * @returns {Promise<Array>} Array of collection objects
 */
export async function getUserCollections(userId, chainId = null) {
  if (!userId) return [];

  let q = query(
    collection(null, COLLECTIONS_PATH),
    where("userId", "==", userId),
    orderBy("createdAt", "desc")
  );

  if (chainId) {
    q = query(
      collection(null, COLLECTIONS_PATH),
      where("userId", "==", userId),
      where("chainId", "==", chainId.toLowerCase()),
      orderBy("createdAt", "desc")
    );
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  }));
}

/**
 * Get collections by wallet address (useful when user isn't logged in)
 * @param {string} walletAddress Wallet address
 * @param {string} [chainId] Optional filter by chain
 * @returns {Promise<Array>} Array of collection objects
 */
export async function getCollectionsByWallet(walletAddress, chainId = null) {
  console.log("[UserCollections] getCollectionsByWallet called:", { walletAddress, chainId });
  
  if (!walletAddress) {
    console.log("[UserCollections] No wallet address provided");
    return [];
  }

  try {
    let q = query(
      collection(null, COLLECTIONS_PATH),
      where("walletAddress", "==", walletAddress.toLowerCase()),
      orderBy("createdAt", "desc")
    );

    if (chainId) {
      console.log("[UserCollections] Filtering by chainId:", chainId.toLowerCase());
      q = query(
        collection(null, COLLECTIONS_PATH),
        where("walletAddress", "==", walletAddress.toLowerCase()),
        where("chainId", "==", chainId.toLowerCase()),
        orderBy("createdAt", "desc")
      );
    }

    const snapshot = await getDocs(q);
    console.log("[UserCollections] Found", snapshot.docs.length, "collections");
    
    const results = snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    }));
    
    console.log("[UserCollections] Results:", results);
    return results;
  } catch (error) {
    console.error("[UserCollections] Query failed:", error);
    // If index error, the error message contains a link to create it
    if (error.message?.includes("index")) {
      console.error("[UserCollections] Missing Firestore index! Check the error above for a link to create it.");
    }
    return [];
  }
}

/**
 * Find a collection by name for a user (case-insensitive)
 * @param {string} userId Firebase user ID
 * @param {string} name Collection name to find
 * @param {string} chainId Chain ID
 * @returns {Promise<Object|null>} Collection object or null
 */
export async function findCollectionByName(userId, name, chainId) {
  const collections = await getUserCollections(userId, chainId);
  const searchName = name.toLowerCase().trim();
  return collections.find((c) => c.name.toLowerCase().trim() === searchName) || null;
}

/**
 * Format collections for ComboboxSelect options
 * @param {Array} collections Array of collection objects
 * @returns {Array} Array of { label, value, hint } objects
 */
export function formatCollectionsForCombobox(collections) {
  return collections.map((c) => ({
    label: c.name,
    value: c.contractAddress,
    hint: `${c.symbol} • ${getChainName(c.chainId)}`,
  }));
}

/**
 * Get the default Unit Marketplace option for the collections dropdown
 * @param {string} chainId Hex chain ID
 * @returns {Object} ComboboxSelect option { label, value, hint, isDefault }
 */
export function getDefaultMarketplaceOption(chainId) {
  return {
    label: "Unit Marketplace",
    value: null, // null = use shared marketplace (no custom collection)
    hint: `Shared collection • ${getChainName(chainId)}`,
    isDefault: true,
  };
}

/**
 * Get human-readable chain name
 * @param {string} chainId Hex chain ID
 * @returns {string} Chain name
 */
function getChainName(chainId) {
  const chains = {
    "0x2105": "Base",
    "0x1": "Ethereum",
    "0x89": "Polygon",
    "0xa": "Optimism",
  };
  return chains[chainId?.toLowerCase()] || "Unknown";
}
